/* eslint-disable react/jsx-props-no-spreading */

import PropTypes from 'prop-types';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import {
  graphql,
  usePreloadedQuery,
} from 'react-relay';

import AdminFormPlaceholder from 'src/components/Admin/AdminFormPlaceholder';
import AdminRetirePortfolioUserMember from 'src/components/Admin/AdminPortfolioUserMember/AdminRetirePortfolioUserMember';
import PlaceholderErrorBoundary from 'src/components/ErrorBoundary/PlaceholderErrorBoundary';
import Loading from 'src/components/Loading';

import RouteErrorThrower from './helpers/RouteErrorThrower';
import getShowRouteKey from './helpers/getShowRouteKey';
import useRouteQueryRetry from './helpers/useRouteQueryRetry';

const messages = defineMessages({
  initialLoadError: {
    id: 'admin.admin_portfolio_user_member.admin_portfolio_user_member_retire.page.initial_load_error.message',
    defaultMessage: 'An error occurred while loading this portfolio user member. Please try again.',
  },
  notFoundTitle: {
    id: 'admin.admin_portfolio_user_member.admin_portfolio_user_member_retire.page.not_found.title',
    defaultMessage: 'Portfolio user member not found',
  },
});

export const adminRetirePortfolioUserMemberRouteQuery = graphql`
  query AdminRetirePortfolioUserMemberRoute_Query($uuid: ID!) {
    portfolioUserMember(uuid: $uuid) {
      ...AdminRetirePortfolioUserMember_portfolioUserMember
    }
  }
`;

/**
 * Renders `AdminRetirePortfolioUserMember` from a manually retried Relay query reference.
 * @param {object} props - Component props.
 * @param {object} props.routeProps - Original found-relay route props.
 * @param {object} props.queryRef - Relay preloaded query reference created by `loadQuery`.
 * @returns {React.ReactNode} admin portfolio user member retire route content.
 */
function AdminRetirePortfolioUserMemberPreloadedQueryRenderer({ routeProps, queryRef }) {
  const queryData = usePreloadedQuery(adminRetirePortfolioUserMemberRouteQuery, queryRef);

  return <AdminRetirePortfolioUserMember {...routeProps} {...queryData} />;
}

AdminRetirePortfolioUserMemberPreloadedQueryRenderer.propTypes = {
  routeProps: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  queryRef: PropTypes.shape({
    dispose: PropTypes.func,
  }).isRequired,
};

/**
 * Hook-safe renderer for the `AdminRetirePortfolioUserMember` route.
 * Keeps the initial found-relay result for first paint, then switches to a
 * `useQueryLoader`-managed query reference when the user retries after an
 * error so Relay can suspend back to the loading state. Nested inside the
 * `AdminLayout` boundary, which covers admin-shell errors.
 * @param {object} props - Component props.
 * @param {object} props.routeProps - Original found-relay route props.
 * @param {Error|null} [props.routeError] - Network-level error from found-relay.
 * @returns {React.ReactNode} admin portfolio user member retire route content.
 */
function AdminRetirePortfolioUserMemberRouteRenderer({ routeProps, routeError = null }) {
  const intl = useIntl();
  const { queryRef, handleRetry, retryDisabled } = useRouteQueryRetry(
    adminRetirePortfolioUserMemberRouteQuery,
    routeProps,
    { logLabel: 'AdminRetirePortfolioUserMemberRoute' },
  );

  return (
    <PlaceholderErrorBoundary
      bodyText={intl.formatMessage(messages.initialLoadError)}
      notFound={{ title: intl.formatMessage(messages.notFoundTitle), backPath: '/admin/portfolios' }}
      placeholderComponent={AdminFormPlaceholder}
      onRetryFunc={handleRetry}
      retryDisabled={retryDisabled}
    >
      <React.Suspense fallback={<Loading />}>
        {routeError && !queryRef ? (
          <RouteErrorThrower error={routeError} />
        ) : null}
        {queryRef ? (
          <AdminRetirePortfolioUserMemberPreloadedQueryRenderer
            routeProps={routeProps}
            queryRef={queryRef}
          />
        ) : (
          <AdminRetirePortfolioUserMember {...routeProps} />
        )}
      </React.Suspense>
    </PlaceholderErrorBoundary>
  );
}

AdminRetirePortfolioUserMemberRouteRenderer.propTypes = {
  routeProps: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  routeError: PropTypes.instanceOf(Error),
};

/**
 * Renders the AdminRetirePortfolioUserMember route state supplied by found-relay.
 * A network error is forwarded to the renderer's `RouteErrorThrower` bridge so
 * the local error boundary can catch it. The renderer is keyed with
 * `getShowRouteKey` (uuid and non-locale query params), so navigating to
 * another member or query string remounts it, clearing the typed confirmation
 * and dropping a retried query reference that would otherwise hide a later
 * route error. The page has no link to its own URL. Authentication is resolved by
 * the owning `AdminLayout` route, not here.
 * @param {object} readyState - Found-relay route render state.
 * @param {Error|null} readyState.error - Route query error.
 * @param {object} readyState.match - Found route match, available while props are pending.
 * @param {object|null} readyState.props - Resolved route query props.
 * @returns {React.ReactNode} AdminRetirePortfolioUserMember route content or loading state.
 */
export function adminRetirePortfolioUserMemberRoute({ error = null, match, props = null }) {
  if (error) {
    const routeProps = props || { match, router: match.router };
    return (
      <AdminRetirePortfolioUserMemberRouteRenderer
        key={getShowRouteKey(routeProps)}
        routeError={error}
        routeProps={routeProps}
      />
    );
  }

  if (!props) {
    return <Loading />;
  }

  return (
    <AdminRetirePortfolioUserMemberRouteRenderer
      key={getShowRouteKey(props)}
      routeProps={props}
    />
  );
}

export default AdminRetirePortfolioUserMemberRouteRenderer;
